import { AlertTriangle, CheckCircle, ExternalLink, Shield } from 'lucide-react';
import { useState } from 'react';

interface PantoneLicenseModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export function PantoneLicenseModal({ isOpen, onAccept, onDecline }: PantoneLicenseModalProps) {
  const [hasScrolled, setHasScrolled] = useState(false);

  if (!isOpen) return null;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 10;
    if (isAtBottom && !hasScrolled) {
      setHasScrolled(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Pantone Connect Licensing Agreement
            </h2>
          </div>
        </div>

        {/* Content */}
        <div
          className="px-6 py-4 overflow-y-auto flex-1"
          onScroll={handleScroll}
        >
          <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-1">
                    Important Licensing Notice
                  </h3>
                  <p className="text-amber-800 dark:text-amber-200">
                    Pantone color data is proprietary and licensed content. By proceeding, you acknowledge
                    and agree to the following terms.
                  </p>
                </div>
              </div>
            </div>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                1. User-Provided Credentials
              </h3>
              <p>
                Access to Pantone color data requires authentication through your personal Pantone Connect
                account. You must have an active, valid Pantone Connect subscription to use this feature.
                Chromis does not provide, store, or distribute Pantone color libraries.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                2. Data Usage and Storage
              </h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Your Pantone Connect credentials are used solely for authentication.</li>
                <li>Access tokens are stored locally in your browser and never transmitted to third parties.</li>
                <li>Pantone color data retrieved during your session is not permanently cached or stored.</li>
                <li>All Pantone data is subject to the terms of your Pantone Connect subscription.</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                3. Access Revocation
              </h3>
              <p>
                Pantone access may be revoked at any time by:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                <li>Logging out from Pantone Connect within Chromis</li>
                <li>Expiration of your Pantone Connect subscription</li>
                <li>Changes to Pantone's licensing terms or API availability</li>
                <li>Authentication failures or token expiration</li>
              </ul>
              <p className="mt-2">
                Upon revocation, all locally stored Pantone authentication data will be immediately cleared.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                4. Compliance Requirements
              </h3>
              <p>You agree to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                <li>Maintain an active Pantone Connect subscription while using this feature</li>
                <li>Comply with all Pantone licensing terms and conditions</li>
                <li>Not attempt to extract, cache, or redistribute Pantone color data</li>
                <li>Use Pantone data only for authorized purposes under your subscription</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                5. Disclaimer
              </h3>
              <p>
                Chromis is not affiliated with, endorsed by, or sponsored by Pantone LLC. Pantone® and
                related trademarks are the property of Pantone LLC. Your use of Pantone Connect is governed
                by Pantone's terms of service, which you can review at:
              </p>
              <a
                href="https://www.pantone.com/connect"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-2"
              >
                <ExternalLink className="w-4 h-4" />
                Pantone Connect Terms of Service
              </a>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                6. Fallback Options
              </h3>
              <p>
                If you do not have a Pantone Connect subscription or choose not to authenticate, Chromis
                provides fallback color matching using open-source datasets (CSS named colors and XKCD color
                survey). These alternatives do not require authentication and are freely available.
              </p>
            </section>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Your Privacy is Protected
                  </h3>
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    Chromis operates entirely in your browser. No Pantone data, credentials, or usage
                    information is transmitted to Chromis servers. All authentication occurs directly between
                    your browser and Pantone Connect.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={onDecline}
              className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Decline
            </button>
            <button
              onClick={onAccept}
              disabled={!hasScrolled}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!hasScrolled ? 'Please scroll to the bottom to accept' : ''}
            >
              Accept and Continue
            </button>
          </div>
          {!hasScrolled && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-right mt-2">
              Please scroll to the bottom to enable the Accept button
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
